// Fiche d'un contrat de maintenance : conditions (modifiables tant que brouillon), périmètre, délais, visites, texte généré,
// signature (client + société), avenant, résiliation. Les champs de prix et le PDF n'existent qu'avec le droit contrats.prix.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, FileDown, FilePen, FileSignature, FileText, ShieldCheck, Trash2, XCircle } from 'lucide-react';
import {
  BRIQUES, EXCLUSIONS, LIBELLES_BRIQUE, LIBELLES_EXCLUSION, LIBELLES_LIEU_CONCLUSION, LIBELLES_MODE_FACTURATION, LIBELLES_MODE_VISITE,
  LIBELLES_NATURE_CLIENT, LIBELLES_PERIODICITE_FACTURATION, LIBELLES_PRIORITE, LIEUX_CONCLUSION, MODES_FACTURATION, PERIODICITES_FACTURATION,
  PRIORITES, type Priorite,
} from '@shared/contrats/contrats.ts';
import type { TraceSignature } from '@shared/bons-intervention.ts';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { date, dateHeure, euros, pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, Onglets, Panneau, Valeur,
  useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { SignatureFigee, ZoneSignature } from '../bons/Signature.tsx';
import { BadgeStatutContrat, BadgeStatutVisite, GardeContrats, montantSaisi } from './commun.tsx';
import type { Apercu, Contrat, DetailContrat, Niveau } from './types.ts';

type Onglet = 'conditions' | 'perimetre' | 'niveaux' | 'visites' | 'texte';
const EN_VIGUEUR = ['signe', 'en_cours', 'a_renouveler'];

export function PageContrat() {
  return <GardeContrats><FicheContrat /></GardeContrats>;
}

function FicheContrat() {
  const { id = '' } = useParams();
  const nav = useNavigate(); const qc = useQueryClient(); const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const [onglet, setOnglet] = useState<Onglet>('conditions');
  const [dialogue, setDialogue] = useState<'' | 'signer' | 'avenant' | 'resilier'>('');
  const q = useQuery({ queryKey: ['contrat', id], queryFn: () => api.get<DetailContrat>(`/api/contrats/${id}`) });
  const apercu = useQuery({ queryKey: ['contrat-apercu', id], queryFn: () => api.get<Apercu>(`/api/contrats/${id}/apercu`), enabled: !!q.data });
  const majDetail = (d: DetailContrat) => { qc.setQueryData(['contrat', id], d); qc.invalidateQueries({ queryKey: ['contrat-apercu', id] }); qc.invalidateQueries({ queryKey: ['contrats'] }); };
  const supprimer = useMutation({
    mutationFn: () => api.del(`/api/contrats/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contrats'] }); notifier('Brouillon supprimé.'); nav('/contrats'); },
    onError: (e) => notifier(messageErreur(e), true),
  });

  if (q.isLoading) return <Chargement />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const { contrat: c, droits, avenant } = q.data;
  const brouillon = c.statut === 'brouillon';
  const enVigueur = EN_VIGUEUR.includes(c.statut);
  const modifiable = brouillon && droits.ecrire;
  const manque = apercu.data?.manque ?? [];

  return (
    <div className="pile-l">
      <EntetePage titre={c.intitule}
        fil={<Link to="/contrats">Contrats de maintenance</Link>}
        sousTitre={<span className="cm-entete-infos">
          <span className="mono">{c.reference}</span>{c.version_no > 1 && <span>version {c.version_no}</span>}
          <span>{q.data.client?.name ?? 'Client'}</span>
          <BadgeStatutContrat statut={c.statut} />{c.demo && <Badge ton="neutre">Démo</Badge>}
        </span>}
        actions={<>
          {brouillon && droits.signer && <Bouton variante="principal" icone={<FileSignature aria-hidden />} disabled={apercu.isLoading || manque.length > 0}
            title={manque.length ? 'Complétez d’abord ce qui manque (onglet « Texte du contrat »).' : undefined} onClick={() => setDialogue('signer')}>Faire signer</Bouton>}
          {enVigueur && droits.ecrire && !avenant && <Bouton icone={<FilePen aria-hidden />} onClick={() => setDialogue('avenant')}>Préparer un avenant</Bouton>}
          {enVigueur && droits.signer && <Bouton variante="fantome" icone={<XCircle aria-hidden />} onClick={() => setDialogue('resilier')}>Résilier</Bouton>}
          {droits.prix && <a className="btn" href={`/api/contrats/${c.id}/pdf`} download><FileDown aria-hidden />PDF</a>}
          {modifiable && <Bouton variante="danger" icone={<Trash2 aria-hidden />} chargement={supprimer.isPending}
            onClick={async () => { if (await confirmer('Supprimer ce brouillon ?', 'Le brouillon, son périmètre et ses délais sont effacés. Un contrat signé, lui, ne se supprime jamais.', 'Supprimer')) supprimer.mutate(); }}>Supprimer</Bouton>}
        </>} />

      {brouillon && manque.length > 0 && (
        <Alerte ton="ambre" titre={`Avant signature : ${pluriel(manque.length, 'point à compléter', 'points à compléter')}`}>
          <ul className="cm-manque">{manque.map((m) => <li key={m.champ + m.message}>{m.message}</li>)}</ul>
        </Alerte>
      )}
      {avenant && (
        <Alerte ton="info" titre={`Avenant en préparation (version ${avenant.version_no})`}
          action={<Link className="btn btn-compact" to={`/contrats/${avenant.id}`}>Ouvrir l’avenant</Link>}>
          Le contrat ci-dessous reste en vigueur jusqu’à la signature de l’avenant.
        </Alerte>
      )}
      {c.remplace_id && c.motif_avenant && <Alerte ton="info" titre="Avenant">Motif : {c.motif_avenant}</Alerte>}
      {!brouillon && <Verification id={c.id} />}

      <Onglets<Onglet> libelle="Sections du contrat" actif={onglet} changer={setOnglet} onglets={[
        { id: 'conditions', libelle: 'Conditions' },
        { id: 'perimetre', libelle: 'Périmètre' },
        { id: 'niveaux', libelle: 'Délais' },
        { id: 'visites', libelle: 'Visites', compteur: q.data.visites.length },
        { id: 'texte', libelle: 'Texte du contrat' },
      ]} />

      {onglet === 'conditions' && (modifiable
        ? <FormConditions detail={q.data} majDetail={majDetail} />
        : <FicheConditions c={c} detail={q.data} />)}
      {onglet === 'perimetre' && <PanneauPerimetre detail={q.data} modifiable={modifiable} majDetail={majDetail} />}
      {onglet === 'niveaux' && <PanneauNiveaux detail={q.data} modifiable={modifiable} majDetail={majDetail} />}
      {onglet === 'visites' && <PanneauVisites detail={q.data} />}
      {onglet === 'texte' && <PanneauTexte apercu={apercu.data} chargement={apercu.isLoading} erreur={apercu.error} />}

      <DialogueSignatureContrat id={c.id} reference={c.reference} ouvert={dialogue === 'signer'} fermer={() => setDialogue('')} fini={(d) => { majDetail(d); setDialogue(''); notifier('Contrat signé : texte figé, échéances et visites créées.'); }} />
      <DialogueAvenantContrat id={c.id} ouvert={dialogue === 'avenant'} fermer={() => setDialogue('')} />
      <DialogueResiliation id={c.id} ouvert={dialogue === 'resilier'} fermer={() => setDialogue('')} fini={(d) => { majDetail(d); setDialogue(''); notifier('Contrat résilié.'); }} />
      {element}
    </div>
  );
}

function Verification({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['contrat-verif', id], queryFn: () => api.get<{ signe: boolean; empreinte?: string; conforme?: boolean }>(`/api/contrats/${id}/verification`) });
  if (!q.data?.signe) return null;
  return q.data.conforme
    ? <p className="petit discret cm-verif"><ShieldCheck size={16} aria-hidden /> Texte signé intact (empreinte <span className="mono">{q.data.empreinte?.slice(0, 16)}…</span>)</p>
    : <Alerte ton="danger" titre="Empreinte du texte signé non conforme">Le texte enregistré ne correspond plus à l’empreinte prise à la signature.</Alerte>;
}

// ─── Conditions ───
const libelleListe = (l: string[], libelles: Record<string, string>) => (l.length ? l.map((x) => libelles[x] ?? x).join(', ') : null);

function FicheConditions({ c, detail }: { c: Contrat; detail: DetailContrat }) {
  const formule = detail.formules.find((f) => f.id === c.formule_id);
  return (
    <Panneau titre="Conditions du contrat">
      <dl className="fiche">
        <dt>Client</dt><dd><Valeur v={detail.client?.name} /> · {LIBELLES_NATURE_CLIENT[c.nature_client]}{detail.client?.siren && <> · <span className="mono">{detail.client.siren}</span></>}</dd>
        <dt>Formule</dt><dd><Valeur v={formule?.nom} inconnu="Sur mesure" /></dd>
        <dt>Conclu</dt><dd>{LIBELLES_LIEU_CONCLUSION[c.lieu_conclusion as keyof typeof LIBELLES_LIEU_CONCLUSION] ?? c.lieu_conclusion}</dd>
        <dt>Durée</dt><dd>du {date(c.date_effet)} au {date(c.date_echeance)} ({c.duree_mois} mois) · {c.reconduction === 'aucune' ? 'sans reconduction' : `reconduction par périodes de ${c.periode_reconduction_mois ?? c.duree_mois} mois`}</dd>
        <dt>Préavis</dt><dd>{c.preavis_jours} jours (dénonciation avant le {date(c.date_limite_denonciation)})</dd>
        <dt>Prestations</dt><dd><Valeur v={libelleListe(c.briques, LIBELLES_BRIQUE)} /></dd>
        <dt>Visites</dt><dd>{pluriel(c.visites_par_an, 'visite sur site', 'visites sur site')} et {pluriel(c.telerevues_par_an, 'revue à distance', 'revues à distance')} par an · fenêtre de ± {c.fenetre_visite_jours} jours</dd>
        <dt>Exclusions</dt><dd><Valeur v={libelleListe(c.exclusions, LIBELLES_EXCLUSION)} inconnu="Aucune" /></dd>
        {c.conditions_particulieres && <><dt>Conditions particulières</dt><dd className="cm-texte">{c.conditions_particulieres}</dd></>}
        <dt>Contact client</dt><dd><Valeur v={c.contact_client} inconnu="Non renseigné" /></dd>
        {'prix_annuel_ht' in c && <>
          <dt>Prix annuel HT</dt><dd>{c.prix_annuel_ht != null ? euros(c.prix_annuel_ht) : <span className="inconnu">À fixer</span>}</dd>
          <dt>Plafond pièces HT</dt><dd>{c.pieces_plafond_ht != null ? euros(c.pieces_plafond_ht) : <span className="inconnu">Aucun</span>}</dd>
          <dt>Facturation</dt><dd><Valeur v={[c.periodicite_facturation && LIBELLES_PERIODICITE_FACTURATION[c.periodicite_facturation as keyof typeof LIBELLES_PERIODICITE_FACTURATION],
            c.mode_facturation && LIBELLES_MODE_FACTURATION[c.mode_facturation as keyof typeof LIBELLES_MODE_FACTURATION]].filter(Boolean).join(', ')} inconnu="À préciser" /></dd>
        </>}
        {c.signe_le && <><dt>Signé le</dt><dd>{dateHeure(c.signe_le)}</dd></>}
        {c.resilie_le && <><dt>Résilié au</dt><dd>{date(c.resilie_le)} — {c.motif_resiliation}</dd></>}
      </dl>
    </Panneau>
  );
}

const entier = (v: string) => (v.trim() === '' ? null : Number.parseInt(v, 10));

function FormConditions({ detail, majDetail }: { detail: DetailContrat; majDetail: (d: DetailContrat) => void }) {
  const notifier = useNotifier();
  const c = detail.contrat; const prix = detail.droits.prix;
  const syndicat = c.nature_client === 'syndicat_copro';
  const init = () => ({
    intitule: c.intitule, formuleId: c.formule_id ?? '', lieuConclusion: c.lieu_conclusion, dateEffet: c.date_effet.slice(0, 10), dureeMois: String(c.duree_mois),
    reconduction: c.reconduction, preavisJours: String(c.preavis_jours), fenetreVisiteJours: String(c.fenetre_visite_jours),
    visitesParAn: String(c.visites_par_an), telerevuesParAn: String(c.telerevues_par_an), briques: c.briques, exclusions: c.exclusions,
    conditionsParticulieres: c.conditions_particulieres ?? '', contactClient: c.contact_client ?? '',
    prixAnnuelHt: c.prix_annuel_ht ?? '', piecesPlafondHt: c.pieces_plafond_ht ?? '', periodiciteFacturation: c.periodicite_facturation ?? '', modeFacturation: c.mode_facturation ?? '',
  });
  const [f, setF] = useState(init);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  useEffect(() => setF(init()), [c.id, c.formule_id]);
  const maj = (k: keyof ReturnType<typeof init>, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const basculer = (k: 'briques' | 'exclusions', v: string) => setF((x) => ({ ...x, [k]: x[k].includes(v) ? x[k].filter((y) => y !== v) : [...x[k], v] }));
  const enregistrer = useMutation({
    mutationFn: () => api.patch<DetailContrat>(`/api/contrats/${c.id}`, {
      intitule: f.intitule.trim(), lieuConclusion: f.lieuConclusion, dateEffet: f.dateEffet, dureeMois: entier(f.dureeMois) ?? c.duree_mois,
      reconduction: f.reconduction, preavisJours: entier(f.preavisJours) ?? c.preavis_jours, fenetreVisiteJours: entier(f.fenetreVisiteJours) ?? c.fenetre_visite_jours,
      visitesParAn: entier(f.visitesParAn) ?? 0, telerevuesParAn: entier(f.telerevuesParAn) ?? 0, briques: f.briques, exclusions: f.exclusions,
      ...(syndicat ? {} : { conditionsParticulieres: f.conditionsParticulieres }), contactClient: f.contactClient,
      ...(prix ? { prixAnnuelHt: montantSaisi(String(f.prixAnnuelHt)), piecesPlafondHt: montantSaisi(String(f.piecesPlafondHt)),
        periodiciteFacturation: f.periodiciteFacturation || null, modeFacturation: f.modeFacturation || null } : {}),
    }),
    onSuccess: (d) => { setErreurs({}); majDetail(d); notifier('Conditions enregistrées.'); },
    onError: (e) => {
      if (e instanceof ErreurApi && Array.isArray(e.details)) setErreurs(Object.fromEntries((e.details as { champ: string; message: string }[]).map((x) => [x.champ, x.message])));
      notifier(messageErreur(e), true);
    },
  });
  const appliquerFormule = useMutation({
    mutationFn: (formuleId: string | null) => api.patch<DetailContrat>(`/api/contrats/${c.id}`, { formuleId }),
    onSuccess: (d) => { majDetail(d); notifier('Formule appliquée : prestations, visites et exclusions reprises.'); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Panneau titre="Conditions du contrat (brouillon)" actions={<Bouton variante="principal" chargement={enregistrer.isPending} onClick={() => enregistrer.mutate()}>Enregistrer</Bouton>}>
      <div className="pile">
        <Champ libelle="Intitulé" value={f.intitule} onChange={(e) => maj('intitule', e.target.value)} maxLength={200} erreur={erreurs.intitule} />
        <div className="grille-form">
          <ChampChoix libelle="Formule" value={f.formuleId} vide="Sur mesure" disabled={appliquerFormule.isPending}
            onChange={(e) => { maj('formuleId', e.target.value); appliquerFormule.mutate(e.target.value || null); }}
            options={detail.formules.map((x) => ({ valeur: x.id, libelle: x.nom }))} aide="Choisir une formule reprend ses prestations, ses visites et ses exclusions." />
          <ChampChoix libelle="Lieu de conclusion" value={f.lieuConclusion} onChange={(e) => maj('lieuConclusion', e.target.value)} erreur={erreurs.lieuConclusion}
            options={LIEUX_CONCLUSION.map((l) => ({ valeur: l, libelle: LIBELLES_LIEU_CONCLUSION[l] }))} />
          <Champ libelle="Date d’effet" type="date" value={f.dateEffet} onChange={(e) => maj('dateEffet', e.target.value)} erreur={erreurs.dateEffet} />
          <Champ libelle="Durée (mois)" type="number" min={1} max={120} value={f.dureeMois} onChange={(e) => maj('dureeMois', e.target.value)} erreur={erreurs.dureeMois} />
          <ChampChoix libelle="Reconduction" value={f.reconduction} onChange={(e) => maj('reconduction', e.target.value)}
            options={[{ valeur: 'periodes_successives', libelle: 'Par périodes successives' }, { valeur: 'aucune', libelle: 'Aucune' }]} />
          <Champ libelle="Préavis de dénonciation (jours)" type="number" min={0} max={730} value={f.preavisJours} onChange={(e) => maj('preavisJours', e.target.value)} erreur={erreurs.preavisJours} />
          <Champ libelle="Visites sur site par an" type="number" min={0} max={12} value={f.visitesParAn} onChange={(e) => maj('visitesParAn', e.target.value)} erreur={erreurs.visitesParAn} />
          <Champ libelle="Revues à distance par an" type="number" min={0} max={12} value={f.telerevuesParAn} onChange={(e) => maj('telerevuesParAn', e.target.value)} erreur={erreurs.telerevuesParAn} />
          <Champ libelle="Fenêtre de visite (± jours)" type="number" min={0} max={90} value={f.fenetreVisiteJours} onChange={(e) => maj('fenetreVisiteJours', e.target.value)} erreur={erreurs.fenetreVisiteJours} />
        </div>
        <fieldset className="cm-cases">
          <legend className="petit">Prestations comprises</legend>
          {BRIQUES.map((b) => (
            <label key={b} className="case"><input type="checkbox" checked={f.briques.includes(b)} onChange={() => basculer('briques', b)} /><span className="petit">{LIBELLES_BRIQUE[b]}</span></label>
          ))}
        </fieldset>
        <fieldset className="cm-cases">
          <legend className="petit">Exclusions</legend>
          {EXCLUSIONS.map((x) => (
            <label key={x} className="case"><input type="checkbox" checked={f.exclusions.includes(x)} onChange={() => basculer('exclusions', x)} /><span className="petit">{LIBELLES_EXCLUSION[x]}</span></label>
          ))}
        </fieldset>
        {syndicat
          ? <p className="petit discret">Syndicat de copropriétaires : pas de conditions particulières en texte libre, seules les clauses du modèle s’appliquent.</p>
          : <ChampTexte libelle="Conditions particulières" value={f.conditionsParticulieres} onChange={(e) => maj('conditionsParticulieres', e.target.value)} rows={3} maxLength={4000} erreur={erreurs.conditionsParticulieres} />}
        <Champ libelle="Contact chez le client" value={f.contactClient} onChange={(e) => maj('contactClient', e.target.value)} maxLength={300} placeholder="Nom, fonction, téléphone" />
        {prix && (
          <div className="grille-form">
            <Champ libelle="Prix annuel HT (€)" inputMode="decimal" value={String(f.prixAnnuelHt)} onChange={(e) => maj('prixAnnuelHt', e.target.value)} erreur={erreurs.prixAnnuelHt} />
            <Champ libelle="Plafond pièces HT (€)" inputMode="decimal" value={String(f.piecesPlafondHt)} onChange={(e) => maj('piecesPlafondHt', e.target.value)} erreur={erreurs.piecesPlafondHt} />
            <ChampChoix libelle="Facturation" value={f.periodiciteFacturation} vide="À préciser" onChange={(e) => maj('periodiciteFacturation', e.target.value)}
              options={PERIODICITES_FACTURATION.map((p) => ({ valeur: p, libelle: LIBELLES_PERIODICITE_FACTURATION[p] }))} />
            <ChampChoix libelle="Échéance" value={f.modeFacturation} vide="À préciser" onChange={(e) => maj('modeFacturation', e.target.value)}
              options={MODES_FACTURATION.map((m) => ({ valeur: m, libelle: LIBELLES_MODE_FACTURATION[m] }))} />
          </div>
        )}
      </div>
    </Panneau>
  );
}

// ─── Périmètre ───
function PanneauPerimetre({ detail, modifiable, majDetail }: { detail: DetailContrat; modifiable: boolean; majDetail: (d: DetailContrat) => void }) {
  const notifier = useNotifier();
  const sites = detail.perimetre.sites;
  const initial = () => sites.flatMap((s) => s.equipements.filter((e) => e.inclus).map((e) => e.id));
  const [inclus, setInclus] = useState<string[]>(initial);
  useEffect(() => setInclus(initial()), [detail]);
  const basculer = (id: string) => setInclus((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const enregistrer = useMutation({
    mutationFn: () => api.put<DetailContrat>(`/api/contrats/${detail.contrat.id}/perimetre`, { siteIds: sites.map((s) => s.id), equipementIds: inclus }),
    onSuccess: (d) => { majDetail(d); notifier('Périmètre enregistré.'); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Panneau titre={`Périmètre : ${pluriel(sites.length, 'site', 'sites')}, ${pluriel(inclus.length, 'équipement couvert', 'équipements couverts')}`}
      actions={modifiable ? <Bouton variante="principal" chargement={enregistrer.isPending} onClick={() => enregistrer.mutate()}>Enregistrer</Bouton> : undefined}>
      <div className="pile">
        <p className="petit discret">Ce qui n’est pas coché est hors contrat.</p>
        {sites.map((s) => (
          <fieldset key={s.id} className="cm-cases">
            <legend><strong>{s.nom}</strong>{s.adresse && <span className="petit discret"> — {s.adresse}</span>}{s.demo && <> <Badge ton="neutre">Démo</Badge></>}</legend>
            {s.equipements.length === 0
              ? <p className="petit discret">Aucun équipement inventorié sur ce site.</p>
              : s.equipements.map((e) => (
                <label key={e.id} className="case">
                  <input type="checkbox" checked={inclus.includes(e.id)} disabled={!modifiable} onChange={() => basculer(e.id)} />
                  <span className="petit">{e.libelle}{e.fabricant && ` · ${e.fabricant}`}{e.reference && ` ${e.reference}`}{e.emplacement && <span className="discret"> · {e.emplacement}</span>}</span>
                </label>
              ))}
          </fieldset>
        ))}
      </div>
    </Panneau>
  );
}

// ─── Délais d'intervention ───
const heures = (min: number | null) => (min == null ? '' : String(Math.round((min / 60) * 100) / 100).replace('.', ','));
const minutes = (h: string) => { const v = Number.parseFloat(h.replace(',', '.')); return h.trim() === '' || !Number.isFinite(v) ? null : Math.max(1, Math.round(v * 60)); };

function PanneauNiveaux({ detail, modifiable, majDetail }: { detail: DetailContrat; modifiable: boolean; majDetail: (d: DetailContrat) => void }) {
  const notifier = useNotifier();
  const parPriorite = (p: Priorite) => detail.niveaux.find((n) => n.priorite === p);
  const init = () => PRIORITES.map((p) => { const n = parPriorite(p); return { priorite: p, actif: !!n, distance: heures(n?.delai_distance_min ?? null), site: heures(n?.delai_site_min ?? null),
    retablissement: heures(n?.delai_retablissement_min ?? null), calendrier: (n?.calendrier ?? 'ouvre') as Niveau['calendrier'], prochaineVisite: n?.prochaine_visite ?? false }; });
  const [l, setL] = useState(init);
  useEffect(() => setL(init()), [detail]);
  const maj = (i: number, k: string, v: unknown) => setL((x) => x.map((n, j) => (j === i ? { ...n, [k]: v } : n)));
  const enregistrer = useMutation({
    mutationFn: () => api.put<DetailContrat>(`/api/contrats/${detail.contrat.id}/niveaux`, l.filter((n) => n.actif).map((n) => ({
      priorite: n.priorite, delaiDistanceMin: minutes(n.distance), delaiSiteMin: minutes(n.site), delaiRetablissementMin: minutes(n.retablissement),
      calendrier: n.calendrier, prochaineVisite: n.prochaineVisite }))),
    onSuccess: (d) => { majDetail(d); notifier('Délais enregistrés.'); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Panneau titre="Délais d’intervention" actions={modifiable ? <Bouton variante="principal" chargement={enregistrer.isPending} onClick={() => enregistrer.mutate()}>Enregistrer</Bouton> : undefined}>
      <div className="pile">
        <p className="petit discret">Délais en heures. « Jours ouvrés » : du lundi au vendredi hors jours fériés, 8 h – 12 h et 14 h – 18 h. Une case vide = pas d’engagement.</p>
        {l.map((n, i) => (
          <fieldset key={n.priorite} className="cm-niveau">
            <legend className="petit"><strong>{LIBELLES_PRIORITE[n.priorite]}</strong></legend>
            {modifiable ? (
              <>
                <label className="case"><input type="checkbox" checked={n.actif} onChange={(e) => maj(i, 'actif', e.target.checked)} /><span className="petit">Engagement pour cette priorité</span></label>
                {n.actif && (
                  <div className="grille-form">
                    <Champ libelle="Prise en main à distance (h)" inputMode="decimal" value={n.distance} onChange={(e) => maj(i, 'distance', e.target.value)} />
                    <Champ libelle="Arrivée sur site (h)" inputMode="decimal" value={n.site} onChange={(e) => maj(i, 'site', e.target.value)} />
                    <Champ libelle="Rétablissement (h)" inputMode="decimal" value={n.retablissement} onChange={(e) => maj(i, 'retablissement', e.target.value)} />
                    <ChampChoix libelle="Décompte" value={n.calendrier} onChange={(e) => maj(i, 'calendrier', e.target.value)}
                      options={[{ valeur: 'ouvre', libelle: 'Heures ouvrées' }, { valeur: 'continu', libelle: '24 h sur 24, 7 j sur 7' }]} />
                    <label className="case"><input type="checkbox" checked={n.prochaineVisite} onChange={(e) => maj(i, 'prochaineVisite', e.target.checked)} /><span className="petit">Traité à la prochaine visite</span></label>
                  </div>
                )}
              </>
            ) : n.actif ? (
              <p className="petit">À distance : <Valeur v={n.distance && `${n.distance} h`} inconnu="—" /> · sur site : <Valeur v={n.site && `${n.site} h`} inconnu="—" /> · rétablissement : <Valeur v={n.retablissement && `${n.retablissement} h`} inconnu="—" />
                {' '}({n.calendrier === 'ouvre' ? 'heures ouvrées' : '24 h sur 24'}){n.prochaineVisite && ' · traité à la prochaine visite'}</p>
            ) : <p className="petit discret">Pas d’engagement.</p>}
          </fieldset>
        ))}
      </div>
    </Panneau>
  );
}

// ─── Visites ───
function PanneauVisites({ detail }: { detail: DetailContrat }) {
  const v = detail.visites;
  return (
    <Panneau serre titre={pluriel(v.length, 'visite prévue', 'visites prévues')}>
      {v.length === 0
        ? <EtatVide icone={<CalendarClock />} titre="Pas encore de visite" texte="Les visites sont calculées à la signature, d’après le nombre de visites par an et la date d’effet." />
        : (
          <div className="liste">
            {v.map((x) => (
              <Link key={x.id} to={`/interventions/visites/${x.id}`} className="liste-element">
                <span className="liste-element-corps">
                  <span className="liste-element-titre">{LIBELLES_MODE_VISITE[x.mode]} n° {x.rang} — {x.site}</span>
                  <span className="petit discret"><span className="mono">{x.reference}</span> · prévue le {date(x.date_due)} (entre le {date(x.fenetre_debut)} et le {date(x.fenetre_fin)})</span>
                  <span className="int-ligne-badges"><BadgeStatutVisite statut={x.statut} enRetard={x.en_retard} /></span>
                </span>
              </Link>
            ))}
          </div>
        )}
    </Panneau>
  );
}

// ─── Texte généré ───
function PanneauTexte({ apercu, chargement, erreur }: { apercu?: Apercu; chargement: boolean; erreur: unknown }) {
  if (chargement) return <Chargement />;
  if (!apercu) return <EtatErreur erreur={erreur} />;
  const t = apercu.contenu;
  return (
    <Panneau titre={apercu.signe ? 'Texte signé' : 'Texte du contrat (aperçu)'}>
      <div className="pile cm-texte-contrat">
        <p className="petit discret">Modèle « {t.modele.nom} », version {t.modele.version} · client : {t.client.nom} ({t.client.nature_libelle})</p>
        {t.clauses.map((cl) => (
          <details key={cl.code} className="cm-clause">
            <summary>
              <span>{cl.titre}</span>
              <span className="int-ligne-badges">
                {cl.obligatoire && <Badge ton="info" icone={false}>Obligatoire</Badge>}
                {cl.statut_source === 'a_confirmer' && <Badge ton="ambre" icone={false}>À confirmer</Badge>}
              </span>
            </summary>
            <p className="cm-texte petit">{cl.texte}</p>
            {cl.source && <p className="tres-petit discret">Source : {cl.source}</p>}
          </details>
        ))}
        {t.conditions_financieres && (
          <p className="petit">Prix annuel HT : {t.conditions_financieres.prix_annuel_ht != null ? euros(t.conditions_financieres.prix_annuel_ht) : 'à fixer'}
            {t.conditions_financieres.pieces_plafond_ht != null && <> · plafond pièces HT : {euros(t.conditions_financieres.pieces_plafond_ht)}</>}</p>
        )}
        {apercu.signe && (
          <div className="bi-grille">
            {t.signature_client && <SignatureFigee trace={t.signature_client.trace} libelle={`Client : ${t.signature_client.nom}, ${t.signature_client.qualite}`} />}
            {t.signature_societe && <SignatureFigee trace={t.signature_societe.trace} libelle={`Société${t.signature_societe.nom ? ` : ${t.signature_societe.nom}` : ''}`} />}
          </div>
        )}
        {t.signe_le && <p className="petit discret">Signé le {dateHeure(t.signe_le)}</p>}
        <p className="tres-petit discret cm-pied"><FileText size={14} aria-hidden /> {t.modele.pied}</p>
      </div>
    </Panneau>
  );
}

// ─── Dialogues ───
function erreurDetaillee(e: unknown) {
  return messageErreur(e) + (e instanceof ErreurApi && Array.isArray(e.details) ? ` ${(e.details as { message: string }[]).map((x) => x.message).join(', ')}` : '');
}

function DialogueSignatureContrat({ id, reference, ouvert, fermer, fini }: { id: string; reference: string; ouvert: boolean; fermer: () => void; fini: (d: DetailContrat) => void }) {
  const notifier = useNotifier();
  const [nom, setNom] = useState(''); const [qualite, setQualite] = useState('');
  const [client, setClient] = useState<TraceSignature | null>(null);
  const [societe, setSociete] = useState<TraceSignature | null>(null);
  useEffect(() => { if (ouvert) { setClient(null); setSociete(null); } }, [ouvert]);
  const pret = !!client && !!societe && !!nom.trim() && !!qualite.trim();
  const signer = useMutation({
    mutationFn: () => api.post<DetailContrat>(`/api/contrats/${id}/signer`, { client: { nom, qualite, trace: client }, societe: { trace: societe } }),
    onSuccess: fini,
    onError: (e) => notifier(erreurDetaillee(e), true),
  });
  return (
    <Dialogue titre={`Faire signer ${reference}`} ouvert={ouvert} fermer={fermer} classe="bi-dialogue-signature"
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" icone={<FileSignature aria-hidden />} disabled={!pret} chargement={signer.isPending} onClick={() => signer.mutate()}>Signer et figer</Bouton></>}>
      <div className="pile">
        <div className="bi-grille">
          <Champ libelle="Nom du signataire (client)" value={nom} onChange={(e) => setNom(e.target.value)} maxLength={200} autoComplete="off" />
          <Champ libelle="Qualité (fonction)" value={qualite} onChange={(e) => setQualite(e.target.value)} maxLength={200} placeholder="Directeur technique, syndic…" />
        </div>
        <ZoneSignature libelle="Signature du client" valeur={client} changer={setClient} />
        <ZoneSignature libelle="Signature de la société" valeur={societe} changer={setSociete} />
        <p className="tres-petit discret">Date et heure : celles du serveur. Une fois signé, le contrat est figé ; toute modification passera par un avenant numéroté.</p>
      </div>
    </Dialogue>
  );
}

function DialogueAvenantContrat({ id, ouvert, fermer }: { id: string; ouvert: boolean; fermer: () => void }) {
  const nav = useNavigate(); const qc = useQueryClient(); const notifier = useNotifier();
  const [motif, setMotif] = useState('');
  const creer = useMutation({
    mutationFn: () => api.post<DetailContrat>(`/api/contrats/${id}/avenant`, { motif: motif.trim() }),
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ['contrat', id] }); qc.invalidateQueries({ queryKey: ['contrats'] }); fermer(); notifier('Avenant créé en brouillon.'); nav(`/contrats/${d.contrat.id}`); },
    onError: (e) => notifier(erreurDetaillee(e), true),
  });
  return (
    <Dialogue titre="Préparer un avenant" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={motif.trim().length < 3} chargement={creer.isPending} onClick={() => creer.mutate()}>Créer l’avenant</Bouton></>}>
      <div className="pile">
        <ChampTexte libelle="Motif de l’avenant" value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} maxLength={2000} />
        <p className="tres-petit discret">L’avenant reprend le contrat en brouillon, avec la même référence et le numéro de version suivant. Le contrat actuel reste valable jusqu’à la signature de l’avenant.</p>
      </div>
    </Dialogue>
  );
}

function DialogueResiliation({ id, ouvert, fermer, fini }: { id: string; ouvert: boolean; fermer: () => void; fini: (d: DetailContrat) => void }) {
  const notifier = useNotifier();
  const [jour, setJour] = useState(''); const [motif, setMotif] = useState('');
  const resilier = useMutation({
    mutationFn: () => api.post<DetailContrat>(`/api/contrats/${id}/resilier`, { date: jour, motif: motif.trim() }),
    onSuccess: fini,
    onError: (e) => notifier(erreurDetaillee(e), true),
  });
  return (
    <Dialogue titre="Résilier le contrat" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="danger" disabled={!jour || motif.trim().length < 3} chargement={resilier.isPending} onClick={() => resilier.mutate()}>Résilier</Bouton></>}>
      <div className="pile">
        <Champ libelle="Date de fin" type="date" value={jour} onChange={(e) => setJour(e.target.value)} />
        <ChampTexte libelle="Motif" value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} maxLength={2000} />
        <p className="tres-petit discret">Les visites prévues après cette date sont annulées. Le texte signé reste conservé.</p>
      </div>
    </Dialogue>
  );
}
